import { useEffect, useState } from 'react';
import {
	Box,
	Chip,
	CircularProgress,
	DialogContent,
	Divider,
	Grid,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import { PrecisionManufacturingOutlined } from '@mui/icons-material';
import {
	faCalendarAlt,
	faFilePdf,
	faHashtag,
	faIndustry,
	faPerson,
	faUser,
	faWarehouse,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	MolePrimaryButton,
	useMoleThemeColor,
} from '../../components/MoleDialogShell';
import { useAuth } from '../../hooks/useAuth';
import { useUI } from '../../providers/MoleUIContext';
import { toast } from 'react-toastify';
import { Util } from '../../util/Util';

import { addMoney, lineTotal } from "../../util/quantity";
interface Props {
	isOpen: boolean;
	toggle: () => void;
	bundle: any;
}

const statusColor = (status?: string) => {
	if (status === 'FINALIZED') return 'success';
	if (status === 'RUNNING') return 'warning';
	return 'info';
};

const ProductionReport = ({ isOpen, toggle, bundle }: Props) => {
	const [record, setRecord] = useState<any>(null);
	const { api } = useAuth();
	const { queueReRun, setLoadingMessage, t } = useUI();
	const themeColor = useMoleThemeColor();
	const batch = bundle?.batch;

	const fetchData = async () => {
		if (!batch) return;
		const res = await api.get(`productions/${batch}`);
		if (res?.status === 200) {
			setRecord(res.data);
			return;
		}
		if (res?.status === 401 || res?.status === 422) {
			queueReRun(fetchData);
			return;
		}
		if (res?.status === 404) {
			toast.warn(t('common:toast.warn.noRecord'));
		}
		toggle();
	};

	const handlePrint = async () => {
		if (!batch) return;
		setLoadingMessage('EXPORT');
		const res = await api.get(`reports/production/${batch}`);
		if (res?.status === 200 && res.data?.data) {
			const pdfData = atob(res.data.data);
			const blob = new Blob([Uint8Array.from(pdfData, (c) => c.charCodeAt(0))], {
				type: 'application/pdf',
			});
			window.open(URL.createObjectURL(blob));
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(handlePrint);
		} else {
			toast.error(t('common:report.failedToLoadReport'));
		}
		setLoadingMessage(null);
	};

	useEffect(() => {
		if (isOpen && batch) {
			setRecord(null);
			fetchData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, batch]);

	const inputs =
		record?.components?.filter((c: any) => c.role === 'INPUT') || [];
	const outputs =
		record?.components?.filter((c: any) => c.role === 'OUTPUT') || [];
	const inputTotal =
		record?.input_amt ??
		inputs.reduce(
			(s: number, c: any) => addMoney(s, lineTotal(c.quantity, c.amount)),
			0
		);
	const outputTotal =
		record?.output_amt ??
		outputs.reduce(
			(s: number, c: any) => addMoney(s, lineTotal(c.quantity, c.amount)),
			0
		);
	const showExpected = record?.status === 'RUNNING' || record?.status === 'FINALIZED';

	const renderLines = (rows: any[]) => (
		<TableContainer
			component={Paper}
			elevation={0}
			sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}
		>
			<Table size="small">
				<TableHead sx={{ bgcolor: 'action.hover' }}>
					<TableRow>
						<TableCell sx={{ fontWeight: 700 }}>{t('common:tables.item')}</TableCell>
						{showExpected && (
							<TableCell align="right" sx={{ fontWeight: 700 }}>
								{t('common:production.expected')}
							</TableCell>
						)}
						<TableCell align="right" sx={{ fontWeight: 700 }}>
							{showExpected
								? t('common:production.actual')
								: t('common:tables.quantity')}
						</TableCell>
						<TableCell align="center" sx={{ fontWeight: 700 }}>
							{t('common:production.unit')}
						</TableCell>
						<TableCell align="right" sx={{ fontWeight: 700 }}>
							{t('common:tables.unitCost')}
						</TableCell>
						<TableCell align="right" sx={{ fontWeight: 700 }}>
							{t('common:tables.lineTotal')}
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map((item: any, idx: number) => (
						<TableRow key={item.id || idx} hover>
							<TableCell sx={{ py: 1.5 }}>
								<Typography variant="body2" fontWeight={600}>
									{item.item_desc}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{t('common:report.code', {
										code: item.item_code || t('common:ui.placeholders.notAvailable'),
									})}
								</Typography>
							</TableCell>
							{showExpected && (
								<TableCell align="right">
									{Util.formatNumber(item.planned_qty ?? item.quantity)}
								</TableCell>
							)}
							<TableCell align="right">{Util.formatNumber(item.quantity)}</TableCell>
							<TableCell align="center">{item.unit || '—'}</TableCell>
							<TableCell align="right">{Util.formatNumber(item.amount)}</TableCell>
							<TableCell align="right" sx={{ fontWeight: 700 }}>
								{Util.formatNumber(
									item.total ?? lineTotal(item.quantity, item.amount)
								)}
							</TableCell>
						</TableRow>
					))}
					{rows.length === 0 && (
						<TableRow>
							<TableCell colSpan={showExpected ? 6 : 5}>
								<Typography color="text.secondary">—</Typography>
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);

	return (
		<MoleDialog
			open={isOpen}
			onClose={toggle}
			maxWidth="md"
			fullWidth
			themeColor={themeColor}
			paperOptions={{ maxHeight: '95vh' }}
		>
			<MoleDialogHeader
				eyebrow={t('common:productionReport.title')}
				title={
					record?.reference || bundle?.reference || t('common:status.loading')
				}
				subtitle={
					record ? (
						<Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
							<Chip
								size="small"
								color={statusColor(record.status) as any}
								label={t(`common:production.status.${String(record.status || 'DRAFT').toLowerCase()}`)}
							/>
							{record.bom_name && (
								<Chip size="small" variant="outlined" label={record.bom_name} />
							)}
						</Stack>
					) : undefined
				}
				icon={<PrecisionManufacturingOutlined />}
				onClose={toggle}
				themeColor={themeColor}
			/>

			<DialogContent sx={{ p: 4 }}>
				{record ? (
					<Box>
						<Grid container spacing={3} sx={{ mb: 3 }}>
							<Grid size={{ xs: 12, sm: 6 }}>
								<Typography
									variant="overline"
									sx={{ color: 'text.secondary', fontWeight: 800 }}
								>
									{t('common:productionReport.details')}
								</Typography>
								<Paper
									variant="outlined"
									sx={{ p: 2, mt: 1, bgcolor: 'background.default', borderStyle: 'dashed' }}
								>
									<Stack spacing={1.5}>
										<DetailItem
											icon={faHashtag}
											label={t('common:tables.reference')}
											value={record.reference}
										/>
										<DetailItem
											icon={faCalendarAlt}
											label={t('common:tables.date')}
											value={Util.formatDate(record.date, 'full')}
										/>
										<DetailItem
											icon={faWarehouse}
											label={t('common:production.productionDepot')}
											value={record.depot || '—'}
										/>
										<DetailItem
											icon={faPerson}
											label={t('common:tables.observer')}
											value={record.client || '—'}
										/>
										<DetailItem
											icon={faUser}
											label={t('common:tables.user')}
											value={record.user || '—'}
										/>
										<DetailItem
											icon={faIndustry}
											label={t('common:production.bom')}
											value={record.bom_name || '—'}
										/>
									</Stack>
								</Paper>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<Paper
									variant="outlined"
									sx={{
										p: 3,
										mt: { xs: 0, sm: 4 },
										borderRadius: 3,
										bgcolor: 'primary.main',
										color: 'white',
									}}
								>
									<Stack spacing={1.5}>
										<Stack direction="row" justifyContent="space-between">
											<Typography variant="body2">
												{t('common:production.scale')}
											</Typography>
											<Typography variant="body2" fontWeight={700}>
												{record.scale || 1}
											</Typography>
										</Stack>
										<Stack direction="row" justifyContent="space-between">
											<Typography variant="body2">
												{t('common:production.inputCost')}
											</Typography>
											<Typography variant="body2" fontWeight={700}>
												{Util.formatNumber(inputTotal)}
											</Typography>
										</Stack>
										<Divider sx={{ borderColor: 'rgba(255,255,255,0.15)' }} />
										<Stack direction="row" justifyContent="space-between">
											<Typography variant="body2">
												{t('common:production.outputValue')}
											</Typography>
											<Typography variant="h6" fontWeight={800}>
												{Util.formatNumber(outputTotal)}
											</Typography>
										</Stack>
									</Stack>
								</Paper>
							</Grid>
						</Grid>

						<Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
							{t('common:production.inputs')} ({inputs.length})
						</Typography>
						{renderLines(inputs)}

						<Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
							{t('common:production.outputs')} ({outputs.length})
						</Typography>
						{renderLines(outputs)}

						{record.description && (
							<Box
								sx={{
									mt: 1,
									p: 2,
									borderRadius: 2,
									bgcolor: 'action.hover',
									borderLeft: '4px solid',
									borderColor: 'info.main',
								}}
							>
								<Typography
									variant="caption"
									fontWeight={800}
									color="info.main"
									sx={{ textTransform: 'uppercase' }}
								>
									{t('common:taskPane.notes')}
								</Typography>
								<Typography variant="body2" sx={{ mt: 0.5 }}>
									{record.description}
								</Typography>
							</Box>
						)}
					</Box>
				) : (
					<Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
						<CircularProgress thickness={5} size={48} sx={{ color: themeColor }} />
						<Typography sx={{ mt: 2, color: 'text.secondary' }}>
							{t('common:report.loadingReport')}
						</Typography>
					</Stack>
				)}
			</DialogContent>

			<MoleDialogActions>
				<MoleCancelButton onClick={toggle}>
					{t('common:ui.buttons.close')}
				</MoleCancelButton>
				<MolePrimaryButton
					startIcon={<FontAwesomeIcon icon={faFilePdf} />}
					onClick={handlePrint}
					disabled={!record}
				>
					{t('common:ui.buttons.exportAsPdf')}
				</MolePrimaryButton>
			</MoleDialogActions>
		</MoleDialog>
	);
};

const DetailItem = ({
	icon,
	label,
	value,
}: {
	icon: any;
	label: string;
	value: string;
}) => (
	<Stack direction="row" spacing={1.5} alignItems="center">
		<FontAwesomeIcon icon={icon} style={{ width: 14, color: '#64748b' }} />
		<Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
			{label}:
		</Typography>
		<Typography variant="body2" fontWeight={600}>
			{value || '—'}
		</Typography>
	</Stack>
);

export default ProductionReport;
